import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import Decimal from 'decimal.js'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import customTheme from 'theme'
import BankIcon from '@assets/images/icons/bank.svg'
import LinkIcon from '@assets/images/icons/weblink.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import BankTransferIcon from '@assets/images/bank_transfer.svg'
import ExpenseService from '@/modules/expense/ExpenseService'
import PaymentDrawerV2 from '@/modules/base/components/payment-drawer-v2/payment-drawer'
import { setBankTransferFormData } from 'redux/ducks/banking/bankTransfer'

import { toast } from '@/modules/base/components/toast/toastConfig'
import ThemeInterface from '@/theme-interface'
import { RECEIPT_TYPES } from '@/modules/base/util/constants'
import RevenueService from '@/modules/revenue/revenueService'
import { PAYMENT_TYPE_LESS_PARTIAL, PAYMENT_TYPE_PAYMENT } from '@/modules/payment/constants'
import BankTransferDrawer from '@/modules/base/components/bank-transfer-drawer/bank-transfer-drawer'
import AssignmentDrawer from '@/modules/base/components/assignment-drawer/assignment-drawer'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  action: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: theme.border,
    borderRadius: '4px',
    padding: 15,
    flex: '1 1 0',
    margin: '0 8px',

    '&:hover': {
      cursor: 'pointer',
      borderColor: theme.primary,
    },
    '&:first-child': {
      marginLeft: 0,
    },
    '&:last-child': {
      marginRight: 0,
    },
  },
  actionLabel: {
    marginLeft: 10,
    color: theme.primary,
  },
  // FLAG:useDocuAi — greyed out because the document says the invoice is already paid.
  actionDisabled: {
    background: theme.backgroundColorDisabled,
    '&:hover': {
      cursor: 'default',
      borderColor: theme.borderColor,
    },
  },
  actionLabelDisabled: {
    color: theme.disabledColor,
  },
}))

const ReceiptPaymentStateActions = (props) => {
  const dispatch = useDispatch()
  const classes = useStyles(props)
  const { type, data, onAdd, onAssign, readOnly, openPaymentDrawer } = props
  const [assignmentDrawerOpen, setAssignmentDrawerOpen] = useState(false)
  const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(openPaymentDrawer || false)
  const [bankTransferDrawerOpen, setBankTransferDrawerOpen] = useState(false)

  const transactionData = useMemo(() => {
    const { type: payeeType, supplierId, customerId, baseDataId, metaData } = data
    return payeeType === 'supplier'
      ? { ...data, type, supplierId: supplierId || baseDataId, iban: metaData?.analysis?.data?.payeeIban }
      : { ...data, type, customerId: customerId || baseDataId, iban: metaData?.analysis?.data?.payeeIban }
  }, [data])

  const checkIfAllowed = (addNew?: boolean) => {
    const { payee, priceTotal, receiptNumber } = data

    if (!priceTotal) {
      toast.showToast({ type: 'error', message: lang.expensePaymentEmptyPriceTotalErrorMessage })
      return false
    } else if (!receiptNumber) {
      toast.showToast({ type: 'error', message: lang.expensePaymentEmptyReceiptNumber })
      return false
    } else if (addNew && !payee) {
      toast.showToast({ type: 'error', message: lang.expensePaymentEmptyPayee })
      return false
    }

    return true
  }

  const handleAssignmentDrawerClose = () => {
    setAssignmentDrawerOpen(false)
  }
  const handleAddClick = () => {
    if (readOnly || !checkIfAllowed(true)) return
    const bankTransferFormData = type === RECEIPT_TYPES.EXPENSE
      ? ExpenseService.createBankTransferFormData(data)
      : RevenueService.createBankTransferFormData(data)

    dispatch(setBankTransferFormData(bankTransferFormData))
    setPaymentDrawerOpen(true)
  }
  const handleAssignClick = () => {
    if (readOnly || !checkIfAllowed()) return
    setAssignmentDrawerOpen(true)
  }
  // FLAG:useDocuAi — the analysed document states it is already paid, so offering to start a
  // bank transfer invites paying the invoice a SECOND time. `data.paymentStatus` is only ever
  // set by the invoice review (build-review-data); every legacy expense/revenue edit passes
  // data without the key, so this is inert outside the review component by construction.
  // Deliberately only "Überweisen": "Offline-Zahlung" and "Zuordnen" RECORD a payment that
  // already happened, which is exactly what a paid document needs.
  const documentSaysPaid = data.paymentStatus === 'paid'

  // FLAG:useDocuAi — the invoice review cannot start a bank transfer AT ALL: its one save boundary
  // (save-document-invoice-review) rejects a `bankTransfer` booking before any request goes out, so
  // the tile could only ever lead to a payment the reviewer entered and then lost on Save. The
  // review records payments that already happened („Offline-Zahlung", „Zuordnen"); paying an
  // invoice is the expense's job, on the expense. `data.reviewPayee` marks review data exactly —
  // it is written by build-review-data and a legacy expense/revenue form never carries the key
  // (the same discriminator redux/ducks/receipt/edit.ts validates on), so nothing outside the
  // review changes by construction.
  const isDocumentInvoiceReview = !!data.reviewPayee
  const showBankTransfer = type === RECEIPT_TYPES.EXPENSE && !isDocumentInvoiceReview

  const handleTransferClick = () => {
    if (readOnly || documentSaysPaid || !checkIfAllowed(true)) return
    setBankTransferDrawerOpen(true)
  }

  const handleAdd = (booking) => {
    if (readOnly) return
    const { type: bookingType, amount, transactionType } = booking

    const sanitizedBooking = {
      ...booking,
      assignments: [{ assignmentType: transactionType, amount }],
    }

    if (![PAYMENT_TYPE_PAYMENT, PAYMENT_TYPE_LESS_PARTIAL].includes(bookingType)) {
      const isPositive = amount > 0
      // parse amount to positive number
      const calculationAmount = isPositive ? amount : amount * -1
      // calculate rest amount
      const restAmount = calculationAmount > data.outstandingAmount
        ? new Decimal(calculationAmount).minus(data.outstandingAmount).toDP(2).toNumber()
        : new Decimal(data.outstandingAmount).minus(calculationAmount).toDP(2).toNumber()
      // calculate amount for main booking
      const bookingAmount = calculationAmount > data.outstandingAmount
        ? new Decimal(calculationAmount).minus(restAmount).toDP(2).toNumber()
        : calculationAmount
      // set amount for main booking depending on the initial amount
      sanitizedBooking.assignments[0].amount = isPositive ? bookingAmount : bookingAmount * -1

      sanitizedBooking.assignments.push({
        assignmentType: bookingType,
        amount: isPositive ? restAmount : restAmount * -1,
      })
    }

    onAdd?.(sanitizedBooking)
    setPaymentDrawerOpen(false)
  }
  const handleAddTransfer = ([booking]) => {
    onAdd?.(booking)
    setBankTransferDrawerOpen(false)
  }
  const handleAssign = (bookings) => {
    if (readOnly) return
    setAssignmentDrawerOpen(false)
    onAssign?.(bookings)
  }

  const labels: { assignBooking: string, payment?: string, bankTransfer?: string} = {
    assignBooking: lang.receipt.payments.actions.assignBooking,
    bankTransfer: lang.receipt.payments.actions.bankTransfer,
    payment: lang.receipt.payments.actions.payment,
  }

  const assignmentDrawer = useMemo(() => {
    if (!assignmentDrawerOpen) { return null }
    return (
      <AssignmentDrawer
        open={assignmentDrawerOpen}
        transaction={transactionData}
        onAssign={handleAssign}
        onClose={handleAssignmentDrawerClose} />
    )
  }, [type, assignmentDrawerOpen, data])

  const banktransferDrawer = useMemo(() => {
    if (!bankTransferDrawerOpen) { return null }

    return (
      <BankTransferDrawer
        open={bankTransferDrawerOpen}
        onAdd={handleAddTransfer}
        onClose={() => setBankTransferDrawerOpen(false)}
        transaction={transactionData}
        withoutPdf
      />
    )
  }, [data, bankTransferDrawerOpen])

  const paymentDrawer = useMemo(() => {
    if (!paymentDrawerOpen) { return null }

    return (
      <PaymentDrawerV2
        open={paymentDrawerOpen}
        isOverDue={false}
        onAdd={handleAdd}
        onClose={() => setPaymentDrawerOpen(false)}
        transaction={transactionData}
        dunnings={[]}
        withoutPdf />
    )
  }, [type, data, paymentDrawerOpen])

  return (
    <>
      <div className={classes.actions}>
        {showBankTransfer && (
          <div
            className={clsx(classes.action, documentSaysPaid && classes.actionDisabled)}
            data-testid='receipt-payment-action-bankTransfer'
            data-disabled={documentSaysPaid ? 'true' : 'false'}
            title={documentSaysPaid ? lang.receipt.payments.actions.bankTransferAlreadyPaid : undefined}
            onClick={handleTransferClick}
          >
            <SVGIcon
              svg={BankTransferIcon}
              height='15px'
              width='15px'
              fill={documentSaysPaid ? customTheme.disabledColor : customTheme.primary}
            />
            <span className={clsx(classes.actionLabel, documentSaysPaid && classes.actionLabelDisabled)}>
              {labels.bankTransfer}
            </span>
          </div>
        )}
        <div className={classes.action} onClick={handleAddClick}>
          <SVGIcon svg={BankIcon} height='15px' width='15px' fill={customTheme.primary} />
          <span className={classes.actionLabel}>{labels.payment}</span>
        </div>
        <div className={classes.action} onClick={handleAssignClick}>
          <SVGIcon svg={LinkIcon} height='15px' width='15px' fill={customTheme.primary} />
          <span className={classes.actionLabel}>{labels.assignBooking}</span>
        </div>
      </div>
      {paymentDrawer}
      {assignmentDrawer}
      {banktransferDrawer}
    </>
  )
}

export default ReceiptPaymentStateActions
